import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";
import CustomError from "@components/Error/CustomError";
import { MetaDescriptorProps } from "@features/seo/utils/types";
import type { Route } from "@route-types/ZhHans/Article/+types/ZhHansArticlePage.tsx";
import ArticleWrapper from "@routes/Article/ArticleWrapper";
import { ArticlePageContext } from "@routes/Article/utils/types";
import { fetchZhHansArticlePageData } from "@routes/ZhHans/Article/ZhHansArticle.server";
import { fetchContext, ResponseType } from "@sphtech/web2-core/ssr";
import { createMetaTags } from "@sphtech/web2-seo-schema";
import { getEnvironment } from "@util/constant";
import { getEnvironmentDomain } from "@util/helpers";
import { HttpStatusCode } from "axios";

/** This is the main entry point component for /:path. */
const ZhHansArticlePage = (): React.ReactElement => {
  const context = useLoaderData<ArticlePageContext>();

  // StatusCode is not 200
  if (context && context.statusCode !== HttpStatusCode.Ok) {
    return <CustomError statusCode={context.statusCode} />;
  }

  // Check if payload is missing
  if (typeof context?.payload === "undefined") {
    return <CustomError statusCode={HttpStatusCode.NotFound} />;
  }

  const { data: articleData, schema } = context.payload;

  return <ArticleWrapper data={articleData} schema={schema} />;
};

export default ZhHansArticlePage;

export const meta = ({ loaderData }: Route.MetaArgs): MetaDescriptorProps[] => {
  const context = loaderData as ArticlePageContext;

  if (context.type !== ResponseType.SUCCESS) return [];
  const data = context.payload;

  if (!data) return [];
  if (!data.metaTags) return [];

  return createMetaTags(data.metaTags);
};

export const loader = async ({ request }: Route.LoaderArgs) => {
  const url = new URL(request.url, `http://localhost`);
  const path = url.pathname;
  const relativePath = path.replace(/^\/zh-hans/, "") || "/";
  const envDomain = getEnvironmentDomain(getEnvironment());
  const absolutePath = `${envDomain}${relativePath}`;

  return await fetchZhHansArticlePageData(absolutePath);
};

export const clientLoader = async (args: ClientLoaderFunctionArgs) => {
  const result = (await fetchContext(args)) as {
    context?: ArticlePageContext;
  };

  if (!result.context) return await args.serverLoader();

  return result.context;
};
